import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {formatDateFromStringWithHour} from '@/utils/formatDate';
import getThemes from '@/services/ThemeService/getThemes';
import getSettings from '@/services/ThemeService/getSettings';
import updateSettings from '@/services/ThemeService/updateSettings';
import {Setting, Theme} from '@/types/theme';
import {getCompanyId} from '@/utils/getCompanyId';
import addTheme from "@/services/ThemeService/addTheme";
import deleteTheme from "@/services/ThemeService/deleteTheme";
import Modal from "../../components/Modal/Modal";
import Alert from "../../components/Alert/Alert";
import ThemeForm from "./ThemeForm";
import ThemeRevertForm from "./ThemeRevertForm";
import previewTheme from "@/components/Theme/ThemeEditor/utils/previewTheme";
import ThemeCustomizerModal from "@/components/Theme/ThemeCustomizerModal";
import Pagination, {PaginationRef} from "@/components/UiElements/Pagination";
import Popover from "@/components/UiElements/PopOver";
import Splash from "@/components/splash/Splash";
import {Button, ListInfoItem, ListRow} from "@/components/UiElements";
import notifyError from "@/utils/notifyError";

const perPage = 40;

type ThemeScrollProps = {
    filter: string;
    orderByDate: boolean;
    reloadThemeList: boolean;
};
type DefaultThemeType = {
    id: string | null;
}

const getThumbnailUrl = (value: unknown) => {
    if (!value) {
        return '';
    }

    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'object') {
        const item = value as { url?: string; image_url?: string; thumbnail_url?: string };
        return item.url || item.image_url || item.thumbnail_url || '';
    }

    return '';
};

const getThemeThumbnail = (theme: Theme) => {
    const themeData = theme as Theme & {
        previewImageUrl?: unknown;
        theme?: Theme['theme'] & {
            thumbnail?: unknown;
            thumbnail_url?: unknown;
            thumbnail_image_url?: unknown;
            preview_image_url?: unknown;
        };
    };

    return (
        getThumbnailUrl(themeData.thumbnail) ||
        getThumbnailUrl(themeData.thumbnail_url) ||
        getThumbnailUrl(themeData.thumbnail_image_url) ||
        getThumbnailUrl(themeData.preview_image_url) ||
        getThumbnailUrl(themeData.previewImageUrl) ||
        getThumbnailUrl(themeData.theme?.thumbnail) ||
        getThumbnailUrl(themeData.theme?.thumbnail_url) ||
        getThumbnailUrl(themeData.theme?.thumbnail_image_url) ||
        getThumbnailUrl(themeData.theme?.preview_image_url) ||
        ''
    );
};

const ThemePreview = ({ theme }: { theme: Theme }) => {
    const thumbnail = getThemeThumbnail(theme);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setFailed(false);
    }, [thumbnail]);

    if (!thumbnail || failed) {
        return (
            <div className="!w-[100px] h-[100px] max-h-[100px] overflow-hidden flex flex-row justify-center items-center rounded-md min-w-[100px] mix-blend-multiply bg-gray-50 border border-gray-200 px-sm text-center text-text-xs text-gray-300">
                Preview not available
            </div>
        );
    }

    return (
        <div className="!w-[100px] h-[100px] max-h-[100px] overflow-hidden flex flex-row justify-center items-center rounded-md min-w-[100px] mix-blend-multiply bg-gray-50 border border-gray-200">
            <img
                src={thumbnail}
                alt={`${theme.title} preview`}
                className="!w-full !h-full object-contain"
                onError={() => setFailed(true)}
            />
        </div>
    );
};

const ThemeScroll = ({
                         filter,
                         orderByDate,
                     reloadThemeList,
                     }: ThemeScrollProps) => {
    const [data, setData] = useState<Theme[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedTheme, setSelectedTheme] = useState<Theme | null>(null);
    const [removeThemeAlert, setRemoveThemeAlert] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showModalRevert, setShowModalRevert] = useState(false);
    const [showCustomizerModal, setShowCustomizerModal] = useState(false);
    const [customizerTheme, setCustomizerTheme] = useState<Theme | null>(null);
    const [defaultTheme, setDefaultTheme] = useState<DefaultThemeType>({id: null});
    const paginationRef = useRef<PaginationRef>(null);
    const isInitialMount = useRef(true);
    //const searchParams = useSearchParams()
    const companyId = getCompanyId();

    const loadThemes = useCallback(async () => {
        setLoading(true);

        try {
            const response: Theme[] = await getThemes();
            setData(response || []);
        } finally {
            setLoading(false);
        }
    }, []);

    const duplicateTheme = async (theme: Theme) => {
        theme.title = theme.title + '-duplicated';
        await addTheme(theme);
        loadThemes();
    };

    const renameTheme = ( theme: Theme) => {
        setSelectedTheme(theme);
        setShowModal(true);
    };

   /* const previewTheme = (theme: Theme) => {
        previewTheme(theme.id)
       /!* const PLUGIN_API_URL = import.meta.env.VITE_3D_PLUGIN_V2
        const orderDetail = { sku: '51000' };
        const storeName = searchParams.get("storeName") || sessionStorage.getItem('storeName');
        const url = `${PLUGIN_API_URL}/?sku=${orderDetail.sku}&store_name=${storeName}&theme=${theme.id}&disable_legal_compliance=true&disable_profanity_check=true&disable_create_new_product=true&disable_add_to_card=true&development=true`;
         window.open(url, '_blank');*!/
    };*/

    const handeRemoveThemeAlert =  (theme: Theme) => {
        if(theme.id===defaultTheme.id){
            notifyError('Failed to remove current theme');
            return;
        }
        setRemoveThemeAlert(true)
        setSelectedTheme(theme)
    };

    const removeTheme = async () => {
          if (selectedTheme) {
              await deleteTheme(selectedTheme.id);
              setRemoveThemeAlert(false)
              loadThemes();
          }


    };

    
    const handleRevertThemeModal = async (theme: Theme) => {
        setShowModalRevert(true)
        setSelectedTheme(theme)
    };

    const openCustomizer = (theme: Theme) => {
        setCustomizerTheme(theme);
        setShowCustomizerModal(true);
    };

    useEffect(() => {
        if (!reloadThemeList) {
            loadThemes();
        }
    }, [loadThemes, reloadThemeList]);
    
    
    const loadSetting = async () => {
        const response: Setting[] = await getSettings();
        const themeSetting=response?.find((item:any)=>item.key==="theme")
        if(themeSetting) setDefaultTheme(themeSetting.value)
    };

    const publishTheme = async (theme:Theme) => {
        const themeReq={
            id:theme.id
        }
        await updateSettings(themeReq);
        loadSetting()
    };

    useEffect(() => {
        if (!companyId) {
            return;
        }
        loadSetting()
    }, []);

    const filteredData = useMemo(() => {
        return filter
            ? data.filter(theme => {
                return Object.values(theme).some(value =>
                    String(value).toLowerCase().includes(filter.toLowerCase())
                );
            })
            : data;
    }, [data, filter]);

    const sortedData = useMemo(() => {
        const currentTheme = filteredData?.find(theme => theme.id === defaultTheme.id);
        const otherThemes = filteredData?.filter(theme => theme.id !== defaultTheme.id);
        // Sort the other themes by created_at date
        const sortedOtherThemes = otherThemes?.sort((a, b) => {
            return orderByDate ? new Date(b.created_at).getTime() - new Date(a.created_at).getTime() : new Date(a.created_at).getTime() - new Date(b.created_at).getTime()

        });
        return currentTheme ? [currentTheme, ...sortedOtherThemes] : sortedOtherThemes;
    }, [filteredData, defaultTheme, orderByDate]);

    const total = useMemo(() => Math.ceil(sortedData.length / perPage), [sortedData]);
    const firstThemeListId = useMemo(() => (
        sortedData.find(theme => theme.id !== defaultTheme.id)?.id || null
    ), [sortedData, defaultTheme.id]);

    const handleGetPageData = useCallback((pageNumber: number) => {
        const start = (pageNumber - 1) * perPage;
        const end = start + perPage;
        return sortedData.slice(start, end);
    }, [sortedData]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        paginationRef.current?.reset();
    }, [sortedData]);


    const closeModal = () => {
        setShowModal(false);
    };
    const closeModalRevert = () => {
        setShowModalRevert(false);
    };

    const closeModalRemoveAlert = () => {
        setRemoveThemeAlert(false);
    };

    const renderThemeMenu = (theme: Theme) => (
        <Popover
            toggle={(open) => (
                <div
                    className={` select-none w-[37px] h-[37px] min-w-[37px] ${open ? 'text-white bg-gray-900' : ' bg-gray-200 hover:bg-gray-700 text-gray-900 hover:text-white'}     rounded-full flex-col justify-center items-center inline-flex font-icon-regular cursor-pointer transition-all`}
                >
                    <div className="text-center ">ellipsis</div>
                </div>
            )}
            placement="bottom left"
            offsetX={0}
            offsetY={10}
            menu={[
                {
                    title: <span className="capitalize">Edit Theme</span>,
                    icon: '',
                    onClick: () => openCustomizer(theme),
                },
                {
                    title: <span className="capitalize">Preview</span>,
                    icon: '',
                    onClick: () => previewTheme(theme.id),
                },
                {
                    title: <span className="capitalize">Rename</span>,
                    icon: '',
                    onClick: () => renameTheme(theme),
                },
                {
                    title: <span className="capitalize">Duplicate</span>,
                    icon: '',
                    onClick: () => duplicateTheme(theme),
                },
                {
                    title: <span className="capitalize">Revert</span>,
                    icon: '',
                    onClick: () => handleRevertThemeModal(theme),
                },
                ...(theme.id !== defaultTheme.id ? [
                    {
                        title: <span className="capitalize">Publish</span>,
                        icon: '',
                        onClick: () => publishTheme(theme),
                    },
                ] : []),
                { separator: true, className: 'my-[10px]' },
                {
                    title: <span className="capitalize">Delete</span>,
                    icon: '',
                    className: 'hover:bg-red-100 text-red-500',
                    onClick: () => handeRemoveThemeAlert(theme),
                },
            ]}
        />
    );

    return (
        <div className="mt-2">
            <Modal isOpen={showModal} onClose={closeModal}  title={"Theme Name"}>
                {selectedTheme && showModal && (
                    <ThemeForm
                        onUpdateSuccess={loadThemes}
                        oldTheme={selectedTheme}
                        closeModal={closeModal}
                    />
                )}
            </Modal>
            <Modal isOpen={showModalRevert} onClose={closeModalRevert}>
                {selectedTheme && showModalRevert && (
                    <ThemeRevertForm
                        onUpdateSuccess={loadThemes}
                        oldTheme={selectedTheme}
                        closeModal={closeModalRevert}
                    />
                )}
            </Modal>
            {removeThemeAlert && <Alert handleCancel={closeModalRemoveAlert} handleOk={removeTheme} button={'Remove Theme'}  cancel={'Cancel'} title={`Remove ${selectedTheme?.title}`} message={"This action can't be undone."} />}
            {customizerTheme && showCustomizerModal && (
                <ThemeCustomizerModal
                    theme={customizerTheme}
                    sku={undefined}
                    isOpen={showCustomizerModal}
                    onClose={() => {
                        setShowCustomizerModal(false);
                        setCustomizerTheme(null);
                    }}
                    onThemeSaved={loadThemes}
                />
            )}


            {loading && <Splash />}

            {(sortedData.length > 0 && total > 0) ? (
                <Pagination
                    ref={paginationRef}
                    theme={(theme: Theme) => {
                        const isActiveTheme = theme.id === defaultTheme.id;

                        return (
                            <>
                                {theme.id === defaultTheme.id && (
                                    <p className="mt-[30px] mb-sm text-text-sm font-medium text-gray-500">
                                        Active Theme
                                    </p>
                                )}
                                {theme.id === firstThemeListId && (
                                    <p className="mt-lg mb-sm text-text-sm font-medium text-gray-500">
                                        Theme List
                                    </p>
                                )}
                                <ListRow
                                    key={theme.id}
                                    title={<span className="capitalize">{theme.title}</span>}
                                    media={(
                                        <>
                                            <div
                                                className="self-center"
                                                onClick={(event) => event.stopPropagation()}
                                            >
                                                {renderThemeMenu(theme)}
                                            </div>
                                            <ThemePreview theme={theme} />
                                        </>
                                    )}
                                    selected={isActiveTheme}
                                    minHeightClassName="min-h-[140px]"
                                    className={isActiveTheme ? 'mb-md' : ''}
                                    onClick={() => openCustomizer(theme)}
                                    actions={!isActiveTheme ? (
                                        <Button
                                            className="shrink-0"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                publishTheme(theme);
                                            }}
                                        >
                                            Publish
                                        </Button>
                                    ) : undefined}
                                >
                                    <ListInfoItem
                                        label="Create Date"
                                        value={formatDateFromStringWithHour(theme.created_at)}
                                        active={isActiveTheme}
                                        className="w-[180px]"
                                    />
                                    <ListInfoItem
                                        label="Change Date"
                                        value={formatDateFromStringWithHour(theme.changed_at)}
                                        active={isActiveTheme}
                                        className="w-[180px]"
                                    />
                                    <ListInfoItem
                                        label="Status"
                                        value={isActiveTheme ? 'Published' : 'Draft'}
                                        active={isActiveTheme}
                                        valueClassName="capitalize"
                                        className="w-[120px]"
                                    />
                                </ListRow>
                            </>
                        );
                    }}
                    total={total}
                    offset={150}
                    getPageData={handleGetPageData}
                />
            ) : (
                !loading && (
                    <div className={'flex flex-row justify-center items-center px-mx py-xl text-gray-300 bg-gray-50 rounded-md mt-xxl gap-lg h-[150px]'}>
                        No themes match your current filters.
                    </div>
                )
            )}
        </div>
    );
};

export default ThemeScroll;
